import { useGetApiLogs, useGetApiLogsStats, type LoggerLogEntry } from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import {
  ArrowLeft,
  RefreshCw,
  AlertCircle,
  FileText,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { Badge, SearchField, Select, type SelectOption } from '@/components/ui';

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

const LOG_LEVELS: LogLevel[] = ['debug', 'info', 'warn', 'error', 'fatal'];

const LEVEL_BADGES: Record<
  LogLevel,
  { variant: 'default' | 'info' | 'warning' | 'error'; label: string }
> = {
  debug: { variant: 'default', label: 'DEBUG' },
  info: { variant: 'info', label: 'INFO' },
  warn: { variant: 'warning', label: 'WARN' },
  error: { variant: 'error', label: 'ERROR' },
  fatal: { variant: 'error', label: 'FATAL' },
};

const TIME_RANGES = [
  { value: '', label: 'All time' },
  { value: '1h', label: 'Last hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

function getStartTimeFromRange(range: string): string | undefined {
  if (!range) return undefined;

  const now = new Date();
  switch (range) {
    case '1h':
      now.setHours(now.getHours() - 1);
      break;
    case '24h':
      now.setHours(now.getHours() - 24);
      break;
    case '7d':
      now.setDate(now.getDate() - 7);
      break;
    case '30d':
      now.setDate(now.getDate() - 30);
      break;
    default:
      return undefined;
  }
  return now.toISOString();
}

function formatTimestamp(timestamp: string | undefined): string {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return timestamp;
  }
}

function formatLogCount(count: number | undefined): string {
  if (count === undefined) return '0';
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toString();
}

interface LogRowProps {
  log: LoggerLogEntry;
  isExpanded: boolean;
  onToggle: () => void;
}

function hasLogFields(fields: unknown): fields is Record<string, unknown> {
  return typeof fields === 'object' && fields !== null && Object.keys(fields).length > 0;
}

function LogRow({ log, isExpanded, onToggle }: LogRowProps) {
  const level = (log.level?.toLowerCase() || 'info') as LogLevel;
  const badge = LEVEL_BADGES[level] || LEVEL_BADGES.info;
  const hasFields = hasLogFields(log.fields);

  return (
    <div className="border-b border-gray-200 last:border-b-0 dark:border-white/[0.06]">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-4 p-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.02]"
      >
        <div className="flex-shrink-0 pt-0.5">
          <Badge variant={badge.variant} size="sm">
            {badge.label}
          </Badge>
        </div>
        <div className="min-w-0 flex-1">
          <p className="break-words font-mono text-sm text-gray-900 dark:text-white">
            {log.message}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-zinc-500">
            <span>{formatTimestamp(log.timestamp)}</span>
            {log.loggerName && (
              <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono dark:bg-zinc-800">
                {log.loggerName}
              </span>
            )}
          </div>
        </div>
        {hasFields && (
          <ChevronDown
            className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform dark:text-zinc-600 ${isExpanded ? 'rotate-180' : ''}`}
          />
        )}
      </button>
      {isExpanded && hasFields && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 dark:border-white/[0.04] dark:bg-[#111113]">
          <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-xs text-gray-600 dark:text-zinc-400">
            {JSON.stringify(log.fields, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export function LogsPage() {
  // Filter state
  const [selectedLevels, setSelectedLevels] = useState<LogLevel[]>([]);
  const [loggerFilter, setLoggerFilter] = useState('');
  const [timeRange, setTimeRange] = useState('24h');
  const [expandedLogIds, setExpandedLogIds] = useState<Set<number>>(new Set());

  // Cursor-based pagination
  const [cursors, setCursors] = useState<string[]>([]);
  const currentCursor = cursors[cursors.length - 1];

  // Build query params
  const queryParams = useMemo(() => {
    const params: {
      level?: string;
      loggerName?: string;
      startTime?: string;
      limit?: number;
      cursor?: string;
    } = {
      limit: 50,
    };

    if (selectedLevels.length > 0) {
      params.level = selectedLevels.join(',');
    }

    if (loggerFilter.trim()) {
      params.loggerName = loggerFilter.trim();
    }

    const startTime = getStartTimeFromRange(timeRange);
    if (startTime) {
      params.startTime = startTime;
    }

    if (currentCursor) {
      params.cursor = currentCursor;
    }

    return params;
  }, [selectedLevels, loggerFilter, timeRange, currentCursor]);

  // Fetch logs and stats
  const {
    data: logsData,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useGetApiLogs(queryParams, {
    query: {
      refetchOnWindowFocus: false,
    },
  });

  const { data: statsData } = useGetApiLogsStats();

  // Handlers
  const toggleLevel = useCallback((level: LogLevel) => {
    setSelectedLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
    );
    setCursors([]); // Reset pagination when filter changes
  }, []);

  const handleLoggerFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLoggerFilter(e.target.value);
    setCursors([]); // Reset pagination when filter changes
  }, []);

  const handleTimeRangeChange = useCallback((value: string) => {
    setTimeRange(value === 'all' ? '' : value);
    setCursors([]); // Reset pagination when filter changes
  }, []);

  // Build select options for time range
  const timeRangeOptions: SelectOption[] = useMemo(() => {
    return TIME_RANGES.map((range) => ({
      value: range.value || 'all',
      label: range.label,
    }));
  }, []);

  const toggleLogExpanded = useCallback((logId: number | undefined) => {
    if (logId === undefined) return;
    setExpandedLogIds((prev) => {
      const next = new Set(prev);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  }, []);

  const loadNextPage = useCallback(() => {
    if (logsData?.nextCursor) {
      setCursors((prev) => [...prev, logsData.nextCursor!]);
    }
  }, [logsData?.nextCursor]);

  const loadPreviousPage = useCallback(() => {
    setCursors((prev) => prev.slice(0, -1));
  }, []);

  const resetFilters = useCallback(() => {
    setSelectedLevels([]);
    setLoggerFilter('');
    setTimeRange('24h');
    setCursors([]);
  }, []);

  const logs = logsData?.logs || [];
  const hasNextPage = !!logsData?.nextCursor;
  const hasPreviousPage = cursors.length > 0;
  const pageNumber = cursors.length + 1;

  return (
    <div className="space-y-6">
      {/* Header with back link */}
      <div>
        <Link
          to={ROUTES.SETTINGS}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Logs
            </h1>
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              View and search system logs
            </p>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50 dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white dark:hover:border-white/[0.1] dark:hover:bg-[#1f1f23]"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats bar */}
      {statsData && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          <StatCard label="Total" value={formatLogCount(statsData.totalLogs)} />
          <StatCard label="Debug" value={formatLogCount(statsData.debugCount)} variant="default" />
          <StatCard label="Info" value={formatLogCount(statsData.infoCount)} variant="info" />
          <StatCard label="Warn" value={formatLogCount(statsData.warnCount)} variant="warning" />
          <StatCard label="Error" value={formatLogCount(statsData.errorCount)} variant="error" />
          <StatCard label="Fatal" value={formatLogCount(statsData.fatalCount)} variant="error" />
        </div>
      )}

      {/* Filters */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/[0.06] dark:bg-[#18181b]">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          {/* Level filter */}
          <div className="flex-1">
            <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-zinc-400">
              Log Level
            </label>
            <div className="flex flex-wrap gap-2">
              {LOG_LEVELS.map((level) => {
                const badge = LEVEL_BADGES[level];
                const isSelected = selectedLevels.includes(level);
                return (
                  <button
                    key={level}
                    type="button"
                    onClick={() => toggleLevel(level)}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                      isSelected
                        ? 'bg-blue-600 text-white ring-2 ring-blue-500/50'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700 dark:hover:text-zinc-300'
                    }`}
                  >
                    {badge.label}
                  </button>
                );
              })}
              {selectedLevels.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedLevels([])}
                  className="text-xs text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Logger name filter */}
          <div className="w-full lg:w-48">
            <SearchField
              label="Logger"
              placeholder="Filter by logger..."
              value={loggerFilter}
              onChange={handleLoggerFilterChange}
            />
          </div>

          {/* Time range filter */}
          <div className="w-full lg:w-40">
            <Select
              label="Time Range"
              value={timeRange || 'all'}
              onChange={handleTimeRangeChange}
              options={timeRangeOptions}
            />
          </div>
        </div>

        {/* Active filters summary */}
        {(selectedLevels.length > 0 || loggerFilter || timeRange !== '24h') && (
          <div className="mt-3 flex items-center gap-2 border-t border-gray-200 pt-3 dark:border-white/[0.06]">
            <span className="text-xs text-gray-500 dark:text-zinc-500">Active filters:</span>
            {selectedLevels.length > 0 && (
              <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-zinc-800 dark:text-zinc-400">
                {selectedLevels.length} level{selectedLevels.length !== 1 ? 's' : ''}
              </span>
            )}
            {loggerFilter && (
              <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-zinc-800 dark:text-zinc-400">
                logger: {loggerFilter}
              </span>
            )}
            {timeRange && timeRange !== '24h' && (
              <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-zinc-800 dark:text-zinc-400">
                {TIME_RANGES.find((r) => r.value === timeRange)?.label}
              </span>
            )}
            <button
              type="button"
              onClick={resetFilters}
              className="ml-auto text-xs text-blue-500 hover:text-blue-400"
            >
              Reset all
            </button>
          </div>
        )}
      </div>

      {/* Log entries */}
      <div className="rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
          </div>
        ) : isError ? (
          <div className="p-6 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
              <AlertCircle className="h-6 w-6 text-red-400" />
            </div>
            <p className="text-sm text-gray-600 dark:text-zinc-400">Failed to load logs</p>
            <button
              onClick={() => refetch()}
              className="mt-3 text-sm text-blue-500 hover:text-blue-400"
            >
              Try again
            </button>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-6 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-800">
              <FileText className="h-6 w-6 text-gray-500 dark:text-zinc-500" />
            </div>
            <p className="text-sm text-gray-600 dark:text-zinc-400">No logs found</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-zinc-600">
              Try adjusting your filters
            </p>
          </div>
        ) : (
          <>
            {/* Log list */}
            <div className="divide-y divide-gray-200 dark:divide-white/[0.06]">
              {logs.map((log) => (
                <LogRow
                  key={log.id}
                  log={log}
                  isExpanded={expandedLogIds.has(log.id!)}
                  onToggle={() => toggleLogExpanded(log.id)}
                />
              ))}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 dark:border-white/[0.06]">
              <span className="text-sm text-gray-500 dark:text-zinc-500">Page {pageNumber}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={loadPreviousPage}
                  disabled={!hasPreviousPage || isFetching}
                  className="flex h-8 items-center gap-1 rounded-lg border border-gray-200 px-3 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/[0.06] dark:text-zinc-400 dark:hover:bg-white/[0.04] dark:hover:text-white"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <button
                  type="button"
                  onClick={loadNextPage}
                  disabled={!hasNextPage || isFetching}
                  className="flex h-8 items-center gap-1 rounded-lg border border-gray-200 px-3 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/[0.06] dark:text-zinc-400 dark:hover:bg-white/[0.04] dark:hover:text-white"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: string;
  variant?: 'default' | 'info' | 'warning' | 'error';
}

function StatCard({ label, value, variant }: StatCardProps) {
  const colorClasses = {
    default: 'text-gray-600 dark:text-zinc-400',
    info: 'text-blue-500 dark:text-blue-400',
    warning: 'text-yellow-500 dark:text-yellow-400',
    error: 'text-red-500 dark:text-red-400',
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-white/[0.06] dark:bg-[#18181b]">
      <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">{label}</p>
      <p
        className={`mt-1 text-lg font-semibold ${variant ? colorClasses[variant] : 'text-gray-900 dark:text-white'}`}
      >
        {value}
      </p>
    </div>
  );
}
